/**
 * MSAL bootstrap — Risk Mgmt Panel.
 *
 * Plain-browser, mypcb-tenant Entra SSO. SysOps users are bank staff
 * (NOT pcbportal external IDs), so we point at tenant
 * `0f60eb50-7ea7-4db3-b65b-0bc945f33568`.
 *
 * Token resource: the `pcbaccountopeningapi` Function App's exposed scope.
 * Per design doc (wave-8.1-sysops-panel-deploy.md), Ron registers
 * `pcb-sysops-panel-spa` in the mypcb tenant and grants delegated access to
 * the API's `access_as_user` (or equivalent) scope.
 */

import {
  PublicClientApplication,
  LogLevel,
  type Configuration,
} from '@azure/msal-browser';

const clientId =
  (import.meta.env.VITE_AAD_CLIENT_ID as string | undefined) ?? '';
const tenantId =
  (import.meta.env.VITE_AAD_TENANT_ID as string | undefined) ??
  '0f60eb50-7ea7-4db3-b65b-0bc945f33568';

/** ROLLED BACK 2026-06-18: requesting api://<backend>/access_as_user triggered
 *  "Need admin approval" (PCB tenant requires ADMIN consent for custom APIs —
 *  user-consent is blocked despite the scope's consentType=User), which broke
 *  sign-in. Back to Graph User.Read (login works without admin consent). The
 *  crypto-validatable token will be sourced from the ID token instead (no
 *  consent) — see getMsalToken — pending the backend ValidateIdentityAsync ship. */
export const API_SCOPES = ['User.Read', 'openid', 'profile', 'email'];

export const MSAL_CONFIG: Configuration = {
  auth: {
    clientId: clientId || '00000000-0000-0000-0000-000000000000',
    authority: `https://login.microsoftonline.com/${tenantId}`,
    // Root mount on the dedicated pcb-risk-mgmt-panel SWA (white-hill).
    // The legacy '/risk-mgmt/' sub-path was retired 2026-05-10 — see memory
    // feedback_swa_deploy_target_unbreakable_rule_20260510.md. Note:
    // matching redirect URI MUST also be registered on the Entra app reg
    // (`pcb-risk-mgmt-panel-spa`); Ron action if not already there.
    redirectUri:
      typeof window !== 'undefined'
        ? `${window.location.origin}/`
        : '/',
    postLogoutRedirectUri:
      typeof window !== 'undefined'
        ? `${window.location.origin}/`
        : '/',
  },
  cache: {
    cacheLocation: 'sessionStorage',
  },
  system: {
    loggerOptions: {
      logLevel: LogLevel.Warning,
      piiLoggingEnabled: false,
      loggerCallback: (level, message) => {
        if (level <= LogLevel.Warning) {
          // eslint-disable-next-line no-console
          console.warn('[risk-mgmt-msal]', message);
        }
      },
    },
  },
};

export const msalInstance = new PublicClientApplication(MSAL_CONFIG);

let msalReadyPromise: Promise<void> | null = null;
export function ensureMsalReady(): Promise<void> {
  if (!msalReadyPromise) {
    msalReadyPromise = (async () => {
      await msalInstance.initialize();
      await msalInstance.handleRedirectPromise({ navigateToLoginRequestUrl: false });
    })();
  }
  return msalReadyPromise;
}

/**
 * Acquire an access token for the v3 Function. Silent first, then a full-page
 * REDIRECT for interactive sign-in.
 *
 * We deliberately do NOT use loginPopup: when the SPA fires it during the
 * on-load auth bootstrap (no user gesture), the browser's popup blocker kills
 * it — "popup_window_error" — and only a manual "Try again" click works. A
 * redirect is never popup-blocked and reuses the existing Edge/Windows work
 * session (Entra sees the browser cookies → auto-SSO or account picker), so
 * authorized staff sign in without the popup nag. handleRedirectPromise() in
 * ensureMsalReady() completes the round-trip on return; the app then acquires
 * the token silently from the cached account.
 */
/** One token request at a time — parallel silent calls raced into interaction_in_progress and sent
 *  requests with no token (2026-10-06, same fix as the admin panel). */
let inFlight: Promise<string> | null = null;

export function getMsalToken(scopes: string[] = API_SCOPES): Promise<string> {
  if (!inFlight) {
    inFlight = acquireIdToken(scopes).finally(() => { inFlight = null; });
  }
  return inFlight;
}

/** Seconds left on an ID token, from its exp claim (0 when unreadable). */
function secondsLeft(claims: Record<string, unknown> | undefined): number {
  const exp = typeof claims?.exp === 'number' ? claims.exp : 0;
  return exp - Math.floor(Date.now() / 1000);
}

async function acquireIdToken(scopes: string[]): Promise<string> {
  await ensureMsalReady();

  const accounts = msalInstance.getAllAccounts();
  const firstAccount = accounts[0];

  if (firstAccount) {
    try {
      let result = await msalInstance.acquireTokenSilent({
        scopes,
        account: firstAccount,
      });
      // Send the ID token (aud = this SPA's client id, tenant-signed, NO Graph
      // nonce) so the backend BearerTokenValidator.ValidateIdentityAsync can
      // CRYPTOGRAPHICALLY validate it (sig + issuer + lifetime). The Graph
      // access token can't be validated by anyone but Graph; the ID token can,
      // and needs no api-scope admin consent. Bill 2026-06-18.
      // MSAL only checks the Graph token's expiry, not the ID token's — refresh an ID token with
      // under 5 minutes left, or the API rejects it (2026-10-06).
      if (secondsLeft(result.idTokenClaims as Record<string, unknown> | undefined) < 300) {
        result = await msalInstance.acquireTokenSilent({ scopes, account: firstAccount, forceRefresh: true });
      }
      return result.idToken;
    } catch {
      // Silent failed — interactive redirect below.
    }
  }

  // Interactive sign-in via full-page redirect (no popup → no popup-blocker).
  await msalInstance.loginRedirect({ scopes });
  // The page navigates to Entra here; this promise never resolves. On return,
  // ensureMsalReady()'s handleRedirectPromise() sets the account and the next
  // getMsalToken() call resolves silently from cache.
  return new Promise<string>(() => { /* unreachable — page redirected */ });
}
